import { useEffect, useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { adminApi } from '@/utils/api'
import type { AdminLogEntry, AdminSystemHealth } from '@/types/admin'

export default function AdminSystemPage() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [health, setHealth] = useState<AdminSystemHealth | null>(null)
  const [logs, setLogs] = useState<AdminLogEntry[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        const [healthRes, logsRes] = await Promise.all([
          adminApi.systemHealth(),
          adminApi.systemLogs({ limit: 20 }),
        ])
        setHealth((healthRes as { data?: AdminSystemHealth }).data || (healthRes as AdminSystemHealth))
        setLogs((logsRes as { data?: AdminLogEntry[] }).data || (logsRes as unknown as AdminLogEntry[]))
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load system info')
      }
    }
    void load()
  }, [])

  const settings = [
    { label: 'Feature Flags', value: 'Enabled' },
    { label: 'SMS Provider', value: 'Twilio (active)' },
    { label: 'WhatsApp', value: 'Degraded' },
    { label: 'Storage', value: 'Healthy' },
  ]

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      <div className='p-4 md:p-6 lg:p-8'>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
          {/* Header */}
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>System</h1>
              <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>System health, logs, and settings.</p>
            </div>
            <div className='hidden lg:flex items-center gap-3'>
              <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                {initials}
              </div>
              <span className='text-[24px] font-bold text-[#353535]'>{displayName}</span>
            </div>
          </div>

          {/* Health & Logs Grid */}
          <div className='grid grid-cols-1 xl:grid-cols-2 gap-6'>
            {/* System Health */}
            <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6'>
              <h3 className='text-[18px] font-bold text-[#151515] mb-4'>System Health</h3>
              <div className='space-y-4'>
                <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
                  <p className='text-[12px] text-[#4e4d4d] mb-1'>Overall Status</p>
                  <p className='text-[18px] font-bold text-[#15803d] capitalize'>
                    {health?.status || 'Healthy'}
                  </p>
                </div>

                <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
                  <p className='text-[12px] text-[#4e4d4d] mb-2'>Database</p>
                  <p className='text-[14px] font-bold text-[#151515] capitalize mb-2'>
                    {health?.database?.status || 'Connected'}
                  </p>
                  {health?.database?.recordCounts && (
                    <div className='space-y-1'>
                      {[
                        { label: 'Users', count: health.database.recordCounts.users },
                        { label: 'Organizations', count: health.database.recordCounts.organizations },
                        { label: 'Couples', count: health.database.recordCounts.couples },
                      ].map((item) => (
                        <div key={item.label} className='flex justify-between text-[12px]'>
                          <span className='text-[#4e4d4d]'>{item.label}:</span>
                          <span className='font-bold text-[#151515]'>{item.count || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
                  <p className='text-[12px] text-[#4e4d4d] mb-2'>Storage</p>
                  <p className='text-[14px] font-bold text-[#151515] capitalize mb-2'>
                    {health?.storage?.status || 'Available'}
                  </p>
                  {health?.storage && (
                    <div className='space-y-1'>
                      <div className='flex justify-between text-[12px]'>
                        <span className='text-[#4e4d4d]'>Used:</span>
                        <span className='font-bold text-[#151515]'>{health.storage.used || 'N/A'}</span>
                      </div>
                      <div className='flex justify-between text-[12px]'>
                        <span className='text-[#4e4d4d]'>Available:</span>
                        <span className='font-bold text-[#151515]'>{health.storage.available || 'N/A'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {health?.services && (
                  <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
                    <p className='text-[12px] text-[#4e4d4d] mb-2'>Services</p>
                    <div className='space-y-1'>
                      {[
                        { label: 'Authentication', status: health.services.authentication },
                        { label: 'Notifications', status: health.services.notifications },
                        { label: 'File Upload', status: health.services.fileUpload },
                      ].map((svc) => (
                        <div key={svc.label} className='flex justify-between text-[12px]'>
                          <span className='text-[#4e4d4d]'>{svc.label}:</span>
                          <span className='font-bold capitalize text-[#15803d]'>{svc.status || 'operational'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Logs */}
            <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6'>
              <h3 className='text-[18px] font-bold text-[#151515] mb-4'>Logs</h3>
              <div className='h-[400px] overflow-y-auto bg-white rounded-[10px] border border-[#e8e8e8] p-4 text-[12px] font-mono text-[#151515] space-y-2'>
                {(logs.length ? logs : [{ level: 'info', message: 'System boot completed' }]).map((log, idx) => (
                  <div key={`${log.timestamp ?? idx}-${idx}`} className='py-1 border-b border-[#eaecf0] last:border-0'>
                    <span className={`font-bold ${log.level === 'error' ? 'text-red-600' : log.level === 'warn' ? 'text-[#d97706]' : 'text-[#15803d]'}`}>
                      [{log.level?.toUpperCase()}]
                    </span>{' '}
                    {log.message}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6 space-y-4'>
            <h3 className='text-[18px] font-bold text-[#151515]'>Settings</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              {settings.map((item) => (
                <div key={item.label} className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
                  <p className='text-[12px] text-[#4e4d4d]'>{item.label}</p>
                  <p className='text-[18px] font-bold text-[#151515]'>{item.value}</p>
                </div>
              ))}
            </div>
            <button className='px-4 py-2 bg-[#151515] hover:bg-[#2a2a2a] text-white font-bold rounded-[8px] text-[14px]'>
              Update Settings
            </button>
          </div>

          {error && <p className='text-[14px] text-red-600'>{error}</p>}
        </div>
      </div>
    </div>
  )
}
